import type { Agent, StartOptions } from "@fingerprint/agent"
import axiosInstance from "@/api/axios"
import getFingerprintProPublicKey from "@/services/getFingerprintProPublicKey"
import type { FingerprintProPublicKeyData } from "@/types/account"
import fingerprintProTracker from "@/services/fingerprintProTracker"

type FingerprintProRegion = NonNullable<StartOptions["region"]>

const DEFAULT_FINGERPRINT_PRO_REGION: FingerprintProRegion = "ap"

const isFingerprintProRegion = (value: string): value is FingerprintProRegion =>
  value === "us" || value === "eu" || value === "ap"

const resolveRegion = (): FingerprintProRegion => {
  const envRegion = import.meta.env.VITE_FINGERPRINT_PRO_REGION
  return envRegion && isFingerprintProRegion(envRegion)
    ? envRegion
    : DEFAULT_FINGERPRINT_PRO_REGION
}

// Backend proxy root (SACRIC-1262) so ad blockers that block *.fpjs.io
// can't break collection. The v4 agent derives both URLs from it:
//   agent script  GET  {root}/web/v4/{apiKey}
//   identification POST {root}/...
// The backend forwards sub-paths verbatim to the Fingerprint hosts configured
// in systemsetting (FingerprintProApiBaseUrl, default ap.api.fpjs.io, which
// also serves the agent script under /web/v4 in v4). Both GET (agent script)
// and POST (identification) passthrough are live, so both requests load
// through the proxy on our own host rather than *.fpjs.io. Passing a plain
// string (not withoutDefault) still keeps the official Fingerprint endpoints
// as an automatic fallback if the proxy is unreachable — retained as a
// resilience safety net (see initAgent for the proxy-only option).
const FINGERPRINT_PRO_PROXY_PATH = "/exchange/member/playerService/fpProxy/ingest"

// Route Fingerprint traffic to a `metrics.<rootDomain>` edge proxy instead of
// the same-origin backend proxy (SACRIC-1262).
// Master switch: flip to false to force every environment back to the backend
// proxy. While true, metrics is used only in the builds where that proxy is
// provisioned (uat / production); local dev and dev / dev-nginx stay on the
// backend proxy.
const ENABLE_METRICS_SUBDOMAIN_ENDPOINT = true
const METRICS_ENABLED_BUILD_MODES = ["uat-s3", "uat-gcp", "prod"]
const USE_METRICS_SUBDOMAIN_ENDPOINT =
  ENABLE_METRICS_SUBDOMAIN_ENDPOINT &&
  METRICS_ENABLED_BUILD_MODES.includes(import.meta.env.MODE)

// The loader derives the agent script URL from this base as
// {base}/web/v4/{apiKey} and the identification request goes to the base
// itself; ?region tells the proxy which Fingerprint ingress region to forward
// to, and must match the region of the workspace the public key belongs to
// (a mismatch is rejected with wrong_region). Note the loader overwrites the
// query when it builds the script URL, so region reaches the proxy only on the
// identification and helper requests, never on the script download.
//
// Pinned to the single proxy infra provisioned for us: every white label points
// at the same host rather than a `metrics.<own domain>` of its own, because
// infra wants all Fingerprint traffic going through one place while they work
// on the setup. PROXY_INTEGRATION_PATH is the path that proxy is mounted on and
// is part of the base, so both the script URL and the ingress URL inherit it.
// The public key still comes from getFingerprintProPublicKey and has to belong
// to the workspace this proxy is registered with. Restore the host swap below
// to go back to a per-domain metrics host.
const PROXY_HOST = "jsdyms.com"
const PROXY_INTEGRATION_PATH = "HsET3AY67I7luHsl"

const resolveMetricsEndpoint = (): string => {
  return `${window.location.protocol}//${PROXY_HOST}/${PROXY_INTEGRATION_PATH}/?region=${resolveRegion()}`
  // const metricsHost = window.location.host.replace(/^[^.]*/, "metrics")
  // return `${window.location.protocol}//${metricsHost}/?region=${resolveRegion()}`
}

// The backend proxy lives on the API host that owns the login session, so
// reuse the axios baseURL — the same resolution as every other API call (see
// handleBaseURL in api/axios): "/api" through the Vite proxy in local dev,
// and the API subdomain (VITE_BASE_API_SUBDOMAIN / VITE_BASE_API_URL) when
// deployed.
const resolveBackendProxyEndpoint = (): string => {
  const apiBaseURL = (axiosInstance.defaults.baseURL ?? "").replace(/\/$/, "")
  return `${apiBaseURL}${FINGERPRINT_PRO_PROXY_PATH}`
}

const resolveProxyEndpoint = (): string =>
  USE_METRICS_SUBDOMAIN_ENDPOINT
    ? resolveMetricsEndpoint()
    : resolveBackendProxyEndpoint()

const buildFppAlertPrefix = (linkedId: string) => `[Alert][${linkedId}]`
const buildFppRateLimitMsg = (linkedId: string) =>
  `${buildFppAlertPrefix(linkedId)} Public Key: too many requests.`

// v4 agent error code emitted when the burst rate limit is hit; translated
// into the TG-friendly rate-limit message (SACRIC-1187).
const RATE_LIMIT_ERROR_CODE = "too_many_requests"

// Identification timeout in ms (SDK default 10_000). Raised to 20s because the
// flow is fire-and-forget (App.vue / useFetchLogin only `.catch`), so users
// never wait on it, while our main user base on weak mobile networks needs a
// longer budget — Fingerprint warns that low timeouts increase identification
// failures on slow connections. Covers only the identification round-trip
// (client_timeout), NOT the agent script load (script_load_fail).
const FINGERPRINT_PRO_GET_TIMEOUT_MS = 20000

const resolvePublicKeyData = async (): Promise<
  FingerprintProPublicKeyData | undefined
> => {
  try {
    return await getFingerprintProPublicKey.handler()
  } catch (error) {
    console.warn("getFingerprintProPublicKey failed", error)
    return undefined
  }
}

let cachedAgent: Agent | undefined

const initAgent = async (apiKey: string, proxyEnabled: boolean): Promise<Agent> => {
  if (cachedAgent) return cachedAgent

  const { start } = await import("@fingerprint/agent")
  const startOptions: StartOptions = {
    apiKey,
    region: resolveRegion()
  }

  // proxyEnabled (from getFingerprintProPublicKey) gates whether Fingerprint
  // traffic is routed through our proxy. When false, the agent uses its native
  // default endpoints (the official Fingerprint hosts). A plain string keeps
  // the official hosts as an automatic fallback; the backend now supports GET
  // (script) + POST passthrough, so switch to
  // withoutDefault(resolveProxyEndpoint()) if you want proxy-only (never touch
  // *.fpjs.io, at the cost of losing that fallback).
  if (proxyEnabled) {
    startOptions.endpoints = resolveProxyEndpoint()
  }

  cachedAgent = start(startOptions)

  return cachedAgent
}

interface FingerprintAgentError extends Error {
  code: string
}

// Local structural mirror of the SDK's isFingerprintError type guard; kept
// local so error formatting still works when the SDK module itself fails to
// load (e.g. chunk download blocked).
const isFingerprintAgentError = (error: unknown): error is FingerprintAgentError =>
  error instanceof Error && error.name === "FingerprintError" && "code" in error

const extractRawErrorMsg = (error: unknown): string => {
  if (error instanceof Error) return error.message
  if (typeof error === "string") return error

  try {
    return JSON.stringify(error)
  } catch {
    return "Unknown Fingerprint agent error"
  }
}

// Translate the rate-limit error code into the SACRIC-1187 TG wording, and
// forward every other error with its machine code and raw message so
// unexpected signals reach TG intact instead of being silently re-labelled.
const formatTrackerErrorMsg = (error: unknown, linkedId: string): string => {
  if (isFingerprintAgentError(error)) {
    if (error.code === RATE_LIMIT_ERROR_CODE) return buildFppRateLimitMsg(linkedId)
    return `${buildFppAlertPrefix(linkedId)} [${error.code}] ${error.message}`
  }
  return `${buildFppAlertPrefix(linkedId)} ${extractRawErrorMsg(error)}`
}

interface FingerprintProSDKResult {
  eventId?: string
  errorMsg?: string
}

const runSDK = async (
  apiKey: string,
  linkedId: string,
  proxyEnabled: boolean
): Promise<FingerprintProSDKResult> => {
  try {
    const agent = await initAgent(apiKey, proxyEnabled)
    const result = await agent.get({
      linkedId,
      tag: { data: "test" },
      timeout: FINGERPRINT_PRO_GET_TIMEOUT_MS
    })

    return { eventId: result.event_id }
  } catch (error) {
    console.warn("Fingerprint agent failed", error)

    return {
      errorMsg: formatTrackerErrorMsg(error, linkedId)
    }
  }
}

const executeFingerprintProFlow = async () => {
  const publicKeyData = await resolvePublicKeyData()
  if (!publicKeyData) return undefined

  const { publicKey, siteId, createdDate, userId, proxyEnabled } = publicKeyData
  const linkedId = `${siteId}_${userId}_${createdDate}`
  const { eventId, errorMsg } = await runSDK(publicKey, linkedId, proxyEnabled)

  return fingerprintProTracker.handler(
    eventId
      ? { eventId, publicKey }
      : {
          errorMsg:
            errorMsg ?? `${buildFppAlertPrefix(linkedId)} Unknown Fingerprint agent error`
        }
  )
}

// In-flight promise cache: App.vue triggers tracking from both a watcher and
// onMounted, and the Fingerprint agent dedups parallel `.get()` calls so the
// same event_id comes back twice. Caching the promise here guarantees the
// tracker is called exactly once per page load, independent of the
// localStorage guard (which can no-op inside sandboxed iframes).
let inFlightFlow: ReturnType<typeof executeFingerprintProFlow> | undefined

export const runFingerprintProFlow = () => {
  // Avoid the `??=` logical-assignment operator: it needs Chrome 85+, but a
  // meaningful share of users are on Chrome 80-84. `?? assignment` short-circuits
  // identically and is supported on Chrome 80.
  inFlightFlow = inFlightFlow ?? executeFingerprintProFlow()
  return inFlightFlow
}

export default runFingerprintProFlow
